import type { RuleOp, RuleRef, WorkflowRule } from './rules';

/**
 * A rule as one readable line, for the collapsed row.
 *
 * The notation the rules were specified in — `upper(lastName) -> write(up_last)` — reads better than any
 * table of the same information, so the summary uses it. A list of rules should be scannable without opening
 * each one, which is the whole reason they collapse.
 */

/**
 * Which kind of reference this is, by which key is PRESENT — not by which is truthy.
 *
 * The editor sets `{ field: '' }` when you pick "form field", and an empty string is falsy: a truthiness test
 * therefore read it back as a literal and the dropdown snapped to "text" before anything could be typed. The
 * distinction is which key exists, not whether it has a value yet.
 */
export function refKind(ref: RuleRef): 'literal' | 'field' | 'ebts' {
  if (ref.field !== undefined) return 'field';
  if (ref.ebts !== undefined) return 'ebts';
  return 'literal';
}

const refText = (ref: RuleRef): string => {
  const kind = refKind(ref);
  if (kind === 'field') return ref.field || '(no field)';
  if (kind === 'ebts') return ref.ebts || '(no field)';
  return JSON.stringify(ref.literal ?? '');
};

export function opText(op: RuleOp): string {
  switch (op.op) {
    case 'upper': case 'lower': case 'trim': case 'crlf':
      return `${op.op}()`;
    case 'append': case 'prepend':
      return `${op.op}(${refText(op)})`;
    case 'padStart': case 'padEnd':
      return `${op.op}(${op.length}${op.pad ? `, ${JSON.stringify(op.pad)}` : ''})`;
    case 'slice':
      return `slice(${op.start}${op.end === undefined ? '' : `, ${op.end}`})`;
    case 'regex':
      return `regex(${JSON.stringify(op.pattern)} → ${JSON.stringify(op.replace)})`;
    case 'write':
      return `write(${op.ebts ?? op.field ?? '?'})`;
    default:
      return 'unknown()';
  }
}

/** `read(lastName) -> upper() -> write(2.018)`, or the label when one is set. */
export function ruleText(rule: WorkflowRule): string {
  const chain = [refText(rule.read), ...(rule.ops ?? []).map(opText)];
  return chain.join(' → ');
}

/** `when hairColor is not empty` — the condition in words, or '' when there is none. */
export function whenText(rule: WorkflowRule): string {
  const w = rule.when;
  if (!w) return '';
  const subject = w.field ?? w.ebts ?? '?';
  const test = w.is ?? 'notEmpty';
  const phrase =
    test === 'empty' ? 'is empty'
      : test === 'notEmpty' ? 'has a value'
      : test === 'equals' ? `is ${JSON.stringify(w.value ?? '')}`
      : `matches ${JSON.stringify(w.value ?? '')}`;
  return `when ${subject} ${w.not ? `does not ${phrase.replace(/^(is|has|matches) /, (m) => ({ 'is ': 'is ', 'has ': 'have ', 'matches ': 'match ' } as Record<string, string>)[m] ?? m)}` : phrase}`;
}
